import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import type { CalibrationPendencyItem } from "@/types/closed-loop.types";
import {
  CAUSE_LABELS,
  PENDENCY_KIND_LABELS,
  PENDENCY_STATUS_LABELS,
  PENDENCY_STATUS_TONES,
} from "./labels";

interface PendencyListProps {
  items: readonly CalibrationPendencyItem[];
  onResolve: (pendency: CalibrationPendencyItem) => void;
}

// Spec 077 (US2/US3 / FR-040) — a fila do trabalho de calibração.
//
// Cada item diz o que precisa ser feito, em que linha, por causa de quê, sobre qual alvo, e há
// quanto tempo espera. A idade não é enfeite: é o que ordena a fila e o que torna visível uma
// pendência que ninguém tocou.
//
// ⚠️ Situação com cor + dot + rótulo, nunca só cor (FR-045). Uma fila filtrada por `RESOLVED` e uma
// por `DISCARDED` renderizariam idênticas para quem não distingue verde de cinza.

/** "hoje" para idade zero — `0 dias` se lê como defeito de cálculo, não como "chegou agora". */
function ageText(days: number): string {
  if (days === 0) return "aberta hoje";
  return days === 1 ? "há 1 dia" : `há ${days} dias`;
}

export function PendencyList({ items, onResolve }: PendencyListProps) {
  const navigate = useNavigate();

  return (
    <ul className="space-y-2">
      {items.map((p) => (
        <li
          key={p.id}
          className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border-subtle p-3"
        >
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[13px] font-medium text-text-primary">
                {PENDENCY_KIND_LABELS[p.kind]}
              </span>
              <Pill tone={PENDENCY_STATUS_TONES[p.status]} withDot>
                {PENDENCY_STATUS_LABELS[p.status]}
              </Pill>
            </div>
            <p className="text-[12px] text-text-secondary">
              {p.lineName} · {ageText(p.ageDays)}
            </p>
            <p className="text-[12px] text-text-secondary">
              Origem: <span className="text-text-primary">{CAUSE_LABELS[p.cause]}</span>
            </p>
            {p.target ? (
              <p className="text-[12px] text-text-secondary">
                Alvo:{" "}
                <span className="text-text-primary">
                  {p.target.machineName} · {p.target.parameterName}
                </span>
              </p>
            ) : null}
            {p.resolutionNote ? (
              <p className="text-[12px] text-text-primary">“{p.resolutionNote}”</p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {/* Só a ABERTA aceita desfecho: os outros três são terminais, e o servidor recusa com
                409 nomeando o que já aconteceu. */}
            {p.status === "OPEN" ? (
              <Button size="sm" onClick={() => onResolve(p)}>
                Encerrar
              </Button>
            ) : null}
            {/* FR-044 — o caminho de volta ao compromisso de origem, COM a apuração à vista:
                `?compromisso=` abre o card certo. Sem o contexto (janela, desvio, evidência) a
                pendência não é acionável — ninguém calibra a partir de um rótulo —, e cair numa
                lista de cards colapsados é ter de adivinhar qual deles gerou este trabalho. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                navigate(`/lines/${p.lineId}/applied-scenarios?compromisso=${p.appliedScenarioId}`)
              }
            >
              Ver o compromisso
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
